import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ChronoEntry } from "@/types/chronology";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormInput } from "@/components/forms/input";
import { Button } from "@/components/ui/button";
import {
  useCheckOutlookEventExistsQuery,
  useCreateOutlookEventMutation,
  useGetCalendarsQuery,
  useGetSuggestedEventLabelsQuery,
} from "@/modules/myCalendar/myCalendar.api";
import { CircleCheck, CircleX, Loader2 } from "lucide-react";
import * as analytics from "@/services/analytics";
import { FormTextArea } from "@/components/forms/textarea";

interface Props {
  onClose: () => void;
  chronoEntryBaseEvent: ChronoEntry;
  documentId: string;
}

interface FormInputs {
  date: string;
  title: string;
  summary: string;
}

const leftPad = (num: number): string => (num < 10 ? `0${num}` : `${num}`);

export const MyCalendarCreateEventDialog: React.FC<Props> = ({ chronoEntryBaseEvent, onClose, documentId }) => {
  const getCalendarsQuery = useGetCalendarsQuery();
  const checkOutlookEventExistsQuery = useCheckOutlookEventExistsQuery(chronoEntryBaseEvent.id);
  const outlookEventExists = checkOutlookEventExistsQuery.data === true;
  const getSuggestedEventLabelsQuery = useGetSuggestedEventLabelsQuery(
    {
      document_id: documentId,
      chronology_record_id: chronoEntryBaseEvent.id,
    },
    { skip: outlookEventExists || checkOutlookEventExistsQuery.status !== "fulfilled" },
  );
  const [createOutlookEvent] = useCreateOutlookEventMutation();

  const [wasSubmitted, setWasSubmitted] = React.useState(false);
  const [wasFailed, setWasFailed] = React.useState(false);

  const day = chronoEntryBaseEvent.day !== -1 ? chronoEntryBaseEvent.day : chronoEntryBaseEvent.nlp_day;
  const month = chronoEntryBaseEvent.month !== -1 ? chronoEntryBaseEvent.month : chronoEntryBaseEvent.nlp_month;
  const year = chronoEntryBaseEvent.year !== -1 ? chronoEntryBaseEvent.year : chronoEntryBaseEvent.nlp_year;
  const methods = useForm<FormInputs>({
    defaultValues: {
      date: `${year}-${leftPad(month)}-${leftPad(day ?? 1)}`,
    },
  });

  React.useEffect(() => {
    if (!getCalendarsQuery.isLoading && getCalendarsQuery.data?.status === "login_required") {
      analytics.sharepointLogin();
      document.location.href = "/pf-auth/oauth/o365-login?redirect_view=chronology-list";
    }
  }, [getCalendarsQuery]);

  React.useEffect(() => {
    if (!getSuggestedEventLabelsQuery.data || getSuggestedEventLabelsQuery.isLoading) {
      return;
    }

    methods.setValue("title", getSuggestedEventLabelsQuery.data.suggested_title);
    methods.setValue("summary", getSuggestedEventLabelsQuery.data.suggested_summary);
  }, [getSuggestedEventLabelsQuery]);

  const onSubmit = async (payload: FormInputs) => {
    const response = await createOutlookEvent({
      title: payload.title,
      summary: payload.summary,
      chronology_record_id: chronoEntryBaseEvent.id,
    });

    if (response.data?.status === "success") {
      methods.reset();
      setWasSubmitted(true);
    } else {
      setWasFailed(true);
    }
  };

  if (outlookEventExists) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent>
          <div className="flex h-full w-full items-center justify-center">
            You already created outlook event for that record
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (getSuggestedEventLabelsQuery.isLoading || getCalendarsQuery.isLoading || checkOutlookEventExistsQuery.isLoading) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent>
          <div className="flex h-full w-full items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (wasSubmitted) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent>
          <div className="h-lg w-lg flex items-center justify-center">
            <CircleCheck className="min-h-[5rem] min-w-[5rem] rounded-full bg-green-100 text-lg text-green-400" />
          </div>

          <DialogHeader className="my-4 flex items-center justify-center">
            <DialogTitle>Calendar invite Sent</DialogTitle>
          </DialogHeader>
          <div className="flex h-full w-full items-center justify-center">
            Calendar invitation has been successfully sent.
          </div>

          <DialogFooter className="mt-4 flex items-center !justify-center">
            <Button type="button" onClick={() => onClose()}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (wasFailed) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent>
          <div className="h-lg w-lg flex items-center justify-center">
            <CircleX className="min-h-[5rem] min-w-[5rem] rounded-full bg-red-100 text-lg text-red-400" />
          </div>
          <DialogHeader className="my-4 flex items-center justify-center">
            <DialogTitle> Calendar Event Error</DialogTitle>
          </DialogHeader>
          <div className="flex h-full w-full items-center justify-center">
            The calendar event could not be created due to a connection issue or insufficient permissions.
          </div>
          <DialogFooter className="mt-4 flex items-center !justify-center">
            <Button type="button" onClick={() => onClose()}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <FormProvider {...methods}>
        <DialogContent className="max-w-xl">
          <form method="post" onSubmit={methods.handleSubmit(onSubmit)}>
            <DialogHeader className="mb-5">
              <DialogTitle>Create Outlook event</DialogTitle>
            </DialogHeader>

            <div className="mb-6 flex flex-col gap-4">
              <div>
                <label htmlFor="prompt" className="mb-3 text-sm font-medium">
                  Date
                </label>
                <FormInput
                  {...methods.register("date", { required: "This field is required" })}
                  placeholder="Date"
                  name="date"
                  disabled
                />
              </div>

              <div className="mb-6 flex flex-col gap-4">
                <div>
                  <label htmlFor="prompt" className="mb-3 text-sm font-medium">
                    Title
                  </label>
                  <FormInput
                    {...methods.register("title", { required: "This field is required" })}
                    placeholder="Title"
                    name="title"
                  />
                </div>

                <div className="flex gap-x-4">
                  <div className="w-full">
                    <label htmlFor="summary" className="mb-3 text-sm font-medium">
                      Summary
                    </label>
                    <FormTextArea
                      {...methods.register("summary", { required: "This field is required" })}
                      name="summary"
                      placeholder="Enter name"
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onClose()}>
                Cancel
              </Button>
              <Button type="submit" disabled={methods.formState.isSubmitting}>
                {methods.formState.isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};
